import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions, ScrollView } from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { useStageTokens } from '../hooks/useStageTokens';
import { useAccessibilityStore } from '../store/useAccessibilityStore';

interface MapPlacementActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

interface UKCountry {
    id: string;
    name: string;
    flag: string;
    capital: string;
    xPercent: number; // 0-100 on map box
    yPercent: number; // 0-100 on map box
    color: string;
}

const UK_COUNTRIES: UKCountry[] = [
    { id: 'scotland', name: 'Scotland', flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', capital: 'Edinburgh', xPercent: 46, yPercent: 18, color: '#3B82F6' },
    { id: 'northern_ireland', name: 'Northern Ireland', flag: '🇬🇧', capital: 'Belfast', xPercent: 18, yPercent: 38, color: '#10B981' },
    { id: 'wales', name: 'Wales', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', capital: 'Cardiff', xPercent: 34, yPercent: 64, color: '#EF4444' },
    { id: 'england', name: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', capital: 'London', xPercent: 58, yPercent: 62, color: '#8B5CF6' },
];

/**
 * MapPlacementActivity — Key-stage adaptive UK map geography activity.
 *
 * KS1: Pre-labelled country cards to place/tap onto the UK map outline zones.
 * KS2: Detailed map with country flags and optional drawing canvas overlay to sketch borders or features.
 */
export const MapPlacementActivity: React.FC<MapPlacementActivityProps> = ({ activity, onComplete, keyStage }) => {
    const isKS2 = keyStage === 'KS2';
    const tokens = useStageTokens(keyStage);
    const highContrast = useAccessibilityStore((s) => s.highContrast);
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    const { completedStrokes, clearStrokes } = useStrokeStore();
    const [placedCountries, setPlacedCountries] = useState<Record<string, string>>({}); // zoneId -> countryId
    const [selectedCountryId, setSelectedCountryId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'map' | 'draw'>('map');

    const availableCountries = useMemo(() => {
        const placedIds = new Set(Object.values(placedCountries));
        return UK_COUNTRIES.filter(c => !placedIds.has(c.id));
    }, [placedCountries]);

    const allPlaced = Object.keys(placedCountries).length === UK_COUNTRIES.length;
    const canSubmit = isKS2
        ? allPlaced || (completedStrokes.length > 0 && Object.keys(placedCountries).length >= 2)
        : allPlaced;

    const handleZonePress = (zoneId: string) => {
        if (!selectedCountryId) {
            // If already occupied, allow clearing/unplacing
            if (placedCountries[zoneId]) {
                const next = { ...placedCountries };
                delete next[zoneId];
                setPlacedCountries(next);
            }
            return;
        }

        // Place selected country into zone
        setPlacedCountries(prev => ({
            ...prev,
            [zoneId]: selectedCountryId,
        }));
        setSelectedCountryId(null);
    };

    const handleSelectCountry = (id: string) => {
        setSelectedCountryId(prev => (prev === id ? null : id));
    };

    const handleReset = () => {
        setPlacedCountries({});
        setSelectedCountryId(null);
        clearStrokes();
    };

    const handleSubmit = () => {
        useStrokeStore.getState().setStructuredData({
            placedCountries,
            correctCount: Object.entries(placedCountries).filter(([zoneId, countryId]) => zoneId === countryId).length,
            totalCountries: UK_COUNTRIES.length,
            strokes: completedStrokes,
        });
        onComplete();
    };

    return (
        <View style={[styles.container, highContrast && styles.containerHighContrast]}>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                        {activity.title || 'Map of the UK'}
                    </Text>
                    {isKS2 && (
                        <View style={styles.modeSwitcher}>
                            <TouchableOpacity
                                style={[styles.switchBtn, activeTab === 'map' && styles.switchBtnActive]}
                                onPress={() => setActiveTab('map')}
                                accessibilityRole="button"
                                accessibilityLabel="Place Labels mode"
                            >
                                <Text style={[styles.switchTxt, activeTab === 'map' && styles.switchTxtActive]}>📍 Labels & Flags</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.switchBtn, activeTab === 'draw' && styles.switchBtnActive]}
                                onPress={() => setActiveTab('draw')}
                                accessibilityRole="button"
                                accessibilityLabel="Draw on Map mode"
                            >
                                <Text style={[styles.switchTxt, activeTab === 'draw' && styles.switchTxtActive]}>✏️ Draw</Text>
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
                <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                    {isKS2
                        ? 'Place the 4 country flags and labels in their correct UK regions. Use Draw mode to mark capital cities.'
                        : 'Tap a country name below, then tap the matching region on the UK map.'}
                </Text>
            </View>

            {/* Main Interactive Map Area */}
            <View style={[styles.contentArea, isLandscape && styles.contentAreaLandscape]}>
                {/* Map Graphic Canvas / Zones */}
                <View style={styles.mapBox}>
                    {/* Stylized UK Map Background Vector Representation */}
                    <View style={styles.ukMapContainer}>
                        {/* Scotland North Region */}
                        <View style={[styles.mapRegion, styles.scotlandShape]} />
                        {/* Northern Ireland Region */}
                        <View style={[styles.mapRegion, styles.niShape]} />
                        {/* Wales Region */}
                        <View style={[styles.mapRegion, styles.walesShape]} />
                        {/* England South Region */}
                        <View style={[styles.mapRegion, styles.englandShape]} />

                        {/* Interactive Drop Targets on the map */}
                        {UK_COUNTRIES.map((country) => {
                            const isOccupied = !!placedCountries[country.id];
                            const occupant = UK_COUNTRIES.find(c => c.id === placedCountries[country.id]);
                            const isCorrect = placedCountries[country.id] === country.id;

                            return (
                                <TouchableOpacity
                                    key={country.id}
                                    style={[
                                        styles.dropZone,
                                        { left: `${country.xPercent}%`, top: `${country.yPercent}%` },
                                        isOccupied && styles.dropZoneOccupied,
                                        isOccupied && isCorrect && styles.dropZoneCorrect,
                                        selectedCountryId && !isOccupied && styles.dropZoneHighlight,
                                    ]}
                                    onPress={() => handleZonePress(country.id)}
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={
                                        isOccupied
                                            ? `Region occupied by ${occupant?.name}. Tap to remove.`
                                            : `Target drop region for ${country.name}`
                                    }
                                >
                                    {isOccupied && occupant ? (
                                        <View style={styles.placedToken}>
                                            <Text style={styles.placedFlag}>{occupant.flag}</Text>
                                            <Text style={styles.placedName} numberOfLines={1}>{occupant.name}</Text>
                                        </View>
                                    ) : (
                                        <View style={styles.emptyTarget}>
                                            <Text style={styles.targetIcon}>📍</Text>
                                            <Text style={styles.targetPrompt}>Tap to place</Text>
                                        </View>
                                    )}
                                </TouchableOpacity>
                            );
                        })}

                        {/* Drawing Canvas Overlay in Draw Mode for KS2 */}
                        {isKS2 && activeTab === 'draw' && (
                            <View style={StyleSheet.absoluteFillObject}>
                                <SkiaCanvas />
                            </View>
                        )}
                    </View>
                </View>

                {/* Country Tokens Tray */}
                <View style={styles.tray}>
                    <Text style={styles.trayTitle}>
                        {availableCountries.length > 0
                            ? selectedCountryId
                                ? 'Now tap a target region on the map ➔'
                                : 'Select a country to place:'
                            : '🎉 All 4 countries placed!'}
                    </Text>

                    <ScrollView
                        horizontal={!isLandscape}
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.tokensList}
                    >
                        {availableCountries.map((c) => {
                            const isSelected = selectedCountryId === c.id;
                            return (
                                <TouchableOpacity
                                    key={c.id}
                                    style={[
                                        styles.countryToken,
                                        isSelected && styles.countryTokenSelected,
                                    ]}
                                    onPress={() => handleSelectCountry(c.id)}
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`${c.name} with flag ${c.flag}`}
                                >
                                    <Text style={styles.countryFlag}>{c.flag}</Text>
                                    <Text style={[styles.countryName, isSelected && styles.countryNameSelected]}>
                                        {c.name}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                </View>
            </View>

            {/* Footer */}
            <View style={styles.footer}>
                <TouchableOpacity
                    style={styles.resetBtn}
                    onPress={handleReset}
                    accessibilityRole="button"
                    accessibilityLabel="Reset map"
                >
                    <Text style={styles.resetTxt}>Reset</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.submitBtn, !canSubmit && styles.disabledBtn]}
                    onPress={handleSubmit}
                    disabled={!canSubmit}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityState={{ disabled: !canSubmit }}
                >
                    <Text style={styles.submitTxt}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 16 },
    containerHighContrast: { backgroundColor: '#000000', borderColor: '#FFFF00', borderWidth: 2 },
    header: { padding: 16, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B' },
    instructions: { fontSize: 14, color: '#64748B', marginTop: 4 },
    modeSwitcher: { flexDirection: 'row', backgroundColor: '#F1F5F9', borderRadius: 10, padding: 4, gap: 4 },
    switchBtn: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 8 },
    switchBtnActive: { backgroundColor: '#3B82F6' },
    switchTxt: { fontSize: 13, fontWeight: 'bold', color: '#64748B' },
    switchTxtActive: { color: '#FFFFFF' },

    contentArea: { flex: 1, padding: 12 },
    contentAreaLandscape: { flexDirection: 'row', gap: 16 },

    mapBox: {
        flex: 1,
        backgroundColor: '#E0F2FE',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: '#BAE6FD',
        overflow: 'hidden',
        position: 'relative',
        minHeight: 280,
    },
    ukMapContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
    },
    mapRegion: {
        position: 'absolute',
        backgroundColor: '#86EFAC',
        borderRadius: 24,
        borderColor: '#22C55E',
        borderWidth: 2,
    },
    scotlandShape: { top: '8%', left: '38%', width: '36%', height: '32%', borderTopLeftRadius: 50, borderTopRightRadius: 40 },
    niShape: { top: '30%', left: '10%', width: '22%', height: '22%', borderRadius: 30 },
    walesShape: { top: '54%', left: '26%', width: '22%', height: '24%', borderRadius: 20 },
    englandShape: { top: '40%', left: '44%', width: '44%', height: '52%', borderBottomRightRadius: 40, borderTopRightRadius: 30 },

    dropZone: {
        position: 'absolute',
        width: 110,
        minHeight: 48,
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#94A3B8',
        borderStyle: 'dashed',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 6,
        paddingVertical: 4,
        transform: [{ translateX: -55 }, { translateY: -24 }],
        zIndex: 10,
    },
    dropZoneHighlight: {
        borderColor: '#3B82F6',
        backgroundColor: '#EFF6FF',
        borderWidth: 3,
    },
    dropZoneOccupied: {
        borderStyle: 'solid',
        borderColor: '#3B82F6',
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    dropZoneCorrect: {
        borderColor: '#10B981',
    },
    emptyTarget: { alignItems: 'center' },
    targetIcon: { fontSize: 14 },
    targetPrompt: { fontSize: 10, fontWeight: 'bold', color: '#64748B' },
    placedToken: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    placedFlag: { fontSize: 18 },
    placedName: { fontSize: 12, fontWeight: 'bold', color: '#1E293B' },

    tray: {
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    trayTitle: { fontSize: 13, fontWeight: 'bold', color: '#475569', marginBottom: 8 },
    tokensList: { flexDirection: 'row', gap: 10, alignItems: 'center' },
    countryToken: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 10,
        gap: 8,
    },
    countryTokenSelected: {
        borderColor: '#3B82F6',
        backgroundColor: '#EFF6FF',
        transform: [{ scale: 1.05 }],
    },
    countryFlag: { fontSize: 20 },
    countryName: { fontSize: 14, fontWeight: 'bold', color: '#1E293B' },
    countryNameSelected: { color: '#2563EB' },

    footer: {
        flexDirection: 'row',
        padding: 14,
        gap: 12,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    resetBtn: {
        flex: 1,
        height: 52,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    resetTxt: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 2,
        height: 52,
        backgroundColor: '#22C55E',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#FFFFFF' },
});
